import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Webhook, Plus, Trash2, Edit, Play, Copy, Loader2, RotateCcw, CheckCircle2, XCircle, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsMeta, setLogsMeta] = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showSecret, setShowSecret] = useState({});
  const [form, setForm] = useState({ url: '', events: [] });
  const [logFilters, setLogFilters] = useState({ webhook_id: '', event: '', status: '' });
  const [logsPage, setLogsPage] = useState(1);

  const fetchWebhooks = async () => {
    try {
      const r = await api.get('/tenant/webhooks');
      setWebhooks(r.data.webhooks || []);
    } catch { toast.error('Gagal memuat webhooks'); }
  };

  const fetchEvents = async () => {
    try {
      const r = await api.get('/tenant/webhook-events');
      setEvents(r.data.events || []);
    } catch {}
  };

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (logFilters.webhook_id) params.append('webhook_id', logFilters.webhook_id);
      if (logFilters.event) params.append('event', logFilters.event);
      if (logFilters.status) params.append('status', logFilters.status);
      const r = await api.get(`/tenant/webhook-logs?${params}`);
      setLogs(r.data.logs || []);
      setLogsMeta({ total: r.data.total, page: r.data.page, total_pages: r.data.total_pages });
    } catch {}
  }, [logFilters]);

  useEffect(() => {
    Promise.all([fetchWebhooks(), fetchEvents()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLogs(logsPage); }, [logsPage, logFilters, fetchLogs]);

  const handleRegister = async () => {
    if (!form.url.trim()) return toast.error('URL harus diisi');
    if (form.events.length === 0) return toast.error('Pilih minimal 1 event');
    setSaving(true);
    try {
      const r = await api.post('/tenant/webhooks', { url: form.url, events: form.events });
      toast.success('Webhook berhasil didaftarkan');
      setShowRegister(false);
      setForm({ url: '', events: [] });
      fetchWebhooks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mendaftarkan webhook'); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/tenant/webhooks/${editing.id}`, {
        url: form.url || undefined,
        events: form.events.length ? form.events : undefined,
        is_active: form.is_active,
      });
      toast.success('Webhook berhasil diupdate');
      setEditing(null);
      fetchWebhooks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal update webhook'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tenant/webhooks/${id}`);
      toast.success('Webhook berhasil dihapus');
      fetchWebhooks();
    } catch { toast.error('Gagal menghapus webhook'); }
    setConfirmDelete(null);
  };

  const handleTest = async (webhook) => {
    setTesting(webhook.id);
    try {
      const r = await api.post(`/tenant/webhooks/${webhook.id}/test`);
      if (r.data.success) {
        toast.success(`Test berhasil! Status: ${r.data.status_code}, ${r.data.duration_ms}ms`);
      } else {
        toast.error(`Test gagal: ${r.data.error || `Status ${r.data.status_code}`}`);
      }
    } catch { toast.error('Gagal mengirim test webhook'); }
    setTesting(null);
  };

  const handleRetry = async (logId) => {
    try {
      const r = await api.post(`/tenant/webhook-logs/${logId}/retry`);
      toast.success(`Retry ${r.data.status}: HTTP ${r.data.response_status || 'error'}`);
      fetchLogs(logsPage);
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry gagal'); }
  };

  const toggleEvent = (evt) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(evt) ? f.events.filter(e => e !== evt) : [...f.events, evt],
    }));
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({ url: w.url, events: [...w.events], is_active: w.is_active });
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Webhooks</h1>
          <p className="text-muted-foreground">Terima notifikasi real-time saat event terjadi di toko Anda</p>
        </div>
        <Button onClick={() => { setForm({ url: '', events: [] }); setShowRegister(true); }}>
          <Plus className="h-4 w-4 mr-2" />Register Webhook
        </Button>
      </div>

      <Tabs defaultValue="endpoints">
        <TabsList>
          <TabsTrigger value="endpoints">Endpoints ({webhooks.length})</TabsTrigger>
          <TabsTrigger value="logs">Delivery Logs ({logsMeta.total})</TabsTrigger>
        </TabsList>

        {/* ─── Endpoints Tab ──────────────────────── */}
        <TabsContent value="endpoints" className="space-y-3 mt-4">
          {webhooks.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Webhook className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Belum ada Webhook</h3>
                <p className="text-muted-foreground mb-4">Daftarkan webhook endpoint untuk menerima event dari toko Anda</p>
                <Button onClick={() => { setForm({ url: '', events: [] }); setShowRegister(true); }}><Plus className="h-4 w-4 mr-2" />Register Webhook</Button>
              </CardContent>
            </Card>
          ) : (
            webhooks.map(w => (
              <Card key={w.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={w.is_active ? 'default' : 'secondary'}>{w.is_active ? 'Active' : 'Inactive'}</Badge>
                        <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">{w.url}</code>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {w.events.map(e => <Badge key={e} variant="outline" className="text-xs">{e}</Badge>)}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" />{w.success_count} success</span>
                        <span className="flex items-center gap-1"><XCircle className="h-3 w-3 text-red-500" />{w.failure_count} failed</span>
                        {w.last_triggered_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Last: {new Date(w.last_triggered_at).toLocaleString('id-ID')}</span>}
                      </div>
                      {/* Secret */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Secret:</span>
                        <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                          {showSecret[w.id] ? w.secret : 'whsec_••••••••••••'}
                        </code>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setShowSecret(s => ({ ...s, [w.id]: !s[w.id] }))}>
                          {showSecret[w.id] ? <span className="text-[10px]">Hide</span> : <span className="text-[10px]">Show</span>}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyText(w.secret)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button variant="outline" size="sm" onClick={() => handleTest(w)} disabled={testing === w.id}>
                        {testing === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}Test
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(w)}><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(w)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* ─── Logs Tab ──────────────────────── */}
        <TabsContent value="logs" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <Select value={logFilters.webhook_id} onValueChange={v => { setLogFilters(f => ({ ...f, webhook_id: v === 'all' ? '' : v })); setLogsPage(1); }}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Filter Webhook" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Webhook</SelectItem>
                {webhooks.map(w => <SelectItem key={w.id} value={w.id}>{new URL(w.url).hostname}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={logFilters.status} onValueChange={v => { setLogFilters(f => ({ ...f, status: v === 'all' ? '' : v })); setLogsPage(1); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Filter Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-4 py-3 font-medium">Event</th>
                      <th className="text-left px-4 py-3 font-medium">URL</th>
                      <th className="text-left px-4 py-3 font-medium">Status</th>
                      <th className="text-left px-4 py-3 font-medium">HTTP</th>
                      <th className="text-left px-4 py-3 font-medium">Duration</th>
                      <th className="text-left px-4 py-3 font-medium">Time</th>
                      <th className="text-left px-4 py-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Belum ada delivery logs</td></tr>
                    ) : logs.map(l => (
                      <tr key={l.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3"><Badge variant="outline" className="text-xs font-mono">{l.event}</Badge></td>
                        <td className="px-4 py-3 font-mono text-xs max-w-[200px] truncate">{l.url}</td>
                        <td className="px-4 py-3">
                          <Badge variant={l.status === 'success' ? 'default' : 'destructive'} className="text-xs">{l.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-xs">{l.response_status || '-'}</td>
                        <td className="px-4 py-3 text-xs">{l.duration_ms}ms</td>
                        <td className="px-4 py-3 text-xs">{new Date(l.created_at).toLocaleString('id-ID')}</td>
                        <td className="px-4 py-3">
                          {l.status === 'failed' && (
                            <Button variant="ghost" size="sm" onClick={() => handleRetry(l.id)}>
                              <RotateCcw className="h-3 w-3 mr-1" />Retry
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {logsMeta.total_pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-muted-foreground">Page {logsMeta.page} of {logsMeta.total_pages} ({logsMeta.total} total)</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}>Previous</Button>
                    <Button variant="outline" size="sm" disabled={logsPage >= logsMeta.total_pages} onClick={() => setLogsPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Register Webhook Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Register Webhook Baru</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Endpoint URL</Label>
              <Input placeholder="https://yourapp.com/webhooks/Tokomoe" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-2 block">Events</Label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-1">
                {events.map(e => (
                  <label key={e.event} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${form.events.includes(e.event) ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="checkbox" className="rounded" checked={form.events.includes(e.event)} onChange={() => toggleEvent(e.event)} />
                    <div>
                      <div className="font-mono text-xs">{e.event}</div>
                      <div className="text-xs text-muted-foreground">{e.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <Button className="w-full" onClick={handleRegister} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Register Webhook
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Webhook Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Webhook</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Endpoint URL</Label>
              <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
              <Label>{form.is_active ? 'Active' : 'Inactive'}</Label>
            </div>
            <div>
              <Label className="mb-2 block">Events</Label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-1">
                {events.map(e => (
                  <label key={e.event} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${form.events.includes(e.event) ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="checkbox" className="rounded" checked={form.events.includes(e.event)} onChange={() => toggleEvent(e.event)} />
                    <div>
                      <div className="font-mono text-xs">{e.event}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <Button className="w-full" onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hapus Webhook?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Webhook ke <code className="bg-slate-100 px-1 rounded">{confirmDelete?.url}</code> akan dihapus permanen.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Batal</Button>
            <Button variant="destructive" onClick={() => handleDelete(confirmDelete.id)}>Hapus</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
